import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
  keyframes,
} from '@mui/material';
import {
  BarChart3,
  FileText,
  TrendingUp,
  Zap,
  LineChart,
  Bot,
  Wrench,
  Building2,
  Package,
  RefreshCw,
  Target,
  ArrowRight,
  DollarSign,
  PanelBottom,
} from 'lucide-react';
import klogo from '../../assets/klogo.png';

const SPIHome = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Animation keyframes
  const fadeInUp = keyframes`
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  const slideInLeft = keyframes`
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  `;

  const pulse = keyframes`
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  `;

  const shimmer = keyframes`
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  `;

  const float = keyframes`
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  `;

  const rotate = keyframes`
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  `;

  const scaleIn = keyframes`
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  `;

  const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    const bigint = parseInt(h, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
  };

  const rgba = (hex, a) => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const cardStyle = (accent) => ({
    position: 'relative',
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: '#111213',
    border: '1px solid transparent',
    backgroundImage: `linear-gradient(#0f0f10, #0f0f10), linear-gradient(140deg, ${rgba(
      accent,
      0.75
    )}, rgba(255,255,255,0.06))`,
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    boxShadow: '0 20px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    animation: `${fadeInUp} 0.6s ease-out`,
  });

  const cardHoverStyle = (accent) => ({
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: `0 28px 60px rgba(0,0,0,0.55), 0 0 30px ${rgba(accent, 0.4)}, 0 0 0 1px rgba(255,255,255,0.04) inset`,
    border: `1px solid ${rgba(accent, 0.6)}`,
    '& .icon-box': {
      transform: 'scale(1.1) rotate(5deg)',
      boxShadow: `0 0 20px ${rgba(accent, 0.6)}`,
    },
    '& .shine-effect': {
      left: '100%',
    },
  });

  const headerWrapStyle = (accentColor, headerBackgroundColor = '#0f0f10') => ({
    position: 'relative',
    padding: '18px 18px 14px',
    borderRadius: '22px 22px 16px 16px',
    overflow: 'hidden',
    background: `
      radial-gradient(140px 110px at 14% -10%, ${rgba(accentColor, 0.5)} 0%, transparent 70%),
      radial-gradient(240px 140px at 100% -30%, ${rgba(accentColor, 0.38)} 0%, transparent 75%),
      linear-gradient(180deg, ${headerBackgroundColor} 0%, ${headerBackgroundColor} 100%)`,
  });

  const statusPillWrap = (accentColor) => ({
    position: 'absolute',
    top: 12,
    right: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    padding: '6px 10px',
    borderRadius: 999,
    border: `1px solid ${accentColor}`,
    background: '#FFFFFF16',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.25,
    color: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(6px)',
    animation: `${pulse} 2s ease-in-out infinite`,
  });

  const statusDot = (color) => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: color,
    boxShadow: `0 0 8px ${color}`,
    animation: `${pulse} 2s ease-in-out infinite`,
  });

  const iconBoxStyle = (accentColor) => ({
    width: 52,
    height: 52,
    borderRadius: 16,
    display: 'grid',
    placeItems: 'center',
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.3))',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
    color: accentColor,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  });

  const products = {
    identifyValue: [
      {
        id: 'spend-pro',
        title: 'Spend.Pro',
        icon: BarChart3,
        description: "AI enabled spend synergy analytics with Spend.pro.",
        link: 'https://app.powerbi.com/groups/me/reports/874f78c2-76f3-452a-921a-2419770a03e6/5ca8d6e8427e63f97398?ctid=5dc645ed-297f-4dca-b0af-2339c71c5388&experience=power-bi',
        accentColor: '#d4a04f',
        headerBackgroundColor: '#24170c',
      },
      {
        id: 'contracts-iq',
        title: 'Contracts IQ',
        icon: FileText,
        description: 'Contract review and T&Cs comparison with Contract IQ.',
        link: 'https://contractpulsestg.ai.kearney.com/',
        accentColor: '#4a9999',
        headerBackgroundColor: '#0f1b1a',
      },
      {
        id: 'benchmarking',
        title: 'Kearney Benchmarking Database',
        icon: LineChart,
        description: 'Rapid spend benchmarking with Kearney Benchmarking DB.',
        link: 'https://rapidinsightsdev.kearney.com/agentKAI',
        accentColor: '#4d79a4',
        headerBackgroundColor: '#0f1724',
      },
      {
        id: 'insights-pricing',
        title: 'Insights Pricing',
        icon: DollarSign,
        description: 'Cost regression and pricing analytics with Insights pricing.',
        link: 'https://insightspricing.cra.kearney.com/login?redirect=/ModelOutcome/53/254',
        accentColor: '#b366b3',
        headerBackgroundColor: '#1f0f1d',
      },
      {
        id: 'rapid-ai',
        title: 'Rapid.AI',
        icon: Zap,
        description: 'Rapid category wave-planning powered by Rapid.AI.',
        link:'https://app.powerbi.com/groups/me/reports/25c690d8-ccc1-46a5-b2a6-51483b88c82b/ReportSectionbc3f8dbb20cb13d5005e?ctid=5dc645ed-297f-4dca-b0af-2339c71c5388&experience=power-bi',
        accentColor: '#e649a5',
        headerBackgroundColor: '#1c0f16',
      },
    ],
    quickWins: [
      {
        id: 'ai-negotiation',
        title: 'Rapid E-Auctions',
        icon: Bot,
        description: 'Rapid E-Auctions.',
        active: false,
        accentColor: '#9966cc',
        headerBackgroundColor: '#1a1024',
      },
      {
        id: 'tail-negotiation',
        title: 'AI assisted tail Negotiation Campaign',
        icon: PanelBottom,
        description: 'AI assisted tail Negotiation Campaign.',
        accentColor: '#7fb0ff',
        headerBackgroundColor: '#0f1c2d',
      },
    ],
    buyBetterWaves: [
      {
        id: 'supplier-pro',
        title: 'Supplier.Pro',
        icon: Building2,
        description: 'Supplier discovery and intelligence with Supplier.Pro.',
        link: 'https://cube.kearney.com/home/insights/48/202',
        accentColor: '#5fbf92',
        headerBackgroundColor: '#0f1f18',
      },
      {
        id: 'commodity-pro',
        title: 'Commodity.Pro',
        icon: Package,
        description: 'Commodity intelligence and impact modeling with Commodity.Pro.',
        link: 'https://app.powerbi.com/groups/me/reports/986ea13d-1a76-4d45-b2ce-4fac4f4b0cbb/6884e32ce900b9e40468?ctid=5dc645ed-297f-4dca-b0af-2339c71c5388&experience=power-bi',
        // link: "https://atkearney-my.sharepoint.com/personal/kkesha02_atkearney_com/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Fkkesha02%5Fatkearney%5Fcom%2FDocuments%2FMicrosoft%20Teams%20Chat%20Files%2FCommodity%2EPro%5Fdemo%203%201%2Epbix&parent=%2Fpersonal%2Fkkesha02%5Fatkearney%5Fcom%2FDocuments%2FMicrosoft%20Teams%20Chat%20Files&ga=1",
        accentColor: '#b29bff',
        headerBackgroundColor: '#1a1530',
      },
      {
        id: 'should-cost',
        title: 'Should Cost Modeling Toolkit',
        icon: Wrench,
        description: 'Data-driven cost models for fact-based negotiation.',
        accentColor: '#f28f4c',
        headerBackgroundColor: '#24160f',
      },
      {
        id: 'coupa',
        title: 'Collaborative Optimization',
        icon: RefreshCw,
        description: 'Run multi-scenario bidding scenarios and optimization.',
        active: false,
        accentColor: '#5cb3ff',
        headerBackgroundColor: '#102035',
      },
      {
        id: 'negotiation-iq',
        title: 'Negotiation IQ',
        icon: Target,
        description: 'Negotiation preparation & factbook creation with Negotiation IQ.',
        link: "https://contractpulsestg.ai.kearney.com/homepage/negotiations",
        accentColor: '#e27557',
        headerBackgroundColor: '#24150f',
      },
    ],
  };

  const handleProductClick = (product) => {
    if (product.link) {
      window.open(product.link, '_blank', 'noopener,noreferrer');
      return;
    }
    alert(`Link to ${product.title} will be provided soon.`);
  };

  const handleContactUs = () => {
    console.log('Contact us clicked');
    alert('Contact form would open here');
  };

  const ProductCard = ({ product, index }) => {
    const Icon = product.icon;
    const accentColor = product.accentColor || '#e649a5';
    const headerBackgroundColor = product.headerBackgroundColor || '#0f0f10';

    return (
      <Box
        onClick={() => handleProductClick(product)}
        sx={{
          ...cardStyle(accentColor),
          cursor: 'pointer',
          animationDelay: `${index * 0.1}s`,
          ':hover': cardHoverStyle(accentColor),
        }}
      >
        {/* Shine effect on hover */}
        <Box
          className="shine-effect"
          sx={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '50%',
            height: '100%',
            background: `linear-gradient(90deg, transparent, ${rgba(accentColor, 0.3)}, transparent)`,
            transition: 'left 0.6s ease',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        
        <Box sx={headerWrapStyle(accentColor, headerBackgroundColor)}>
          {product.active !== false && (
            <Box sx={statusPillWrap(accentColor)}>
              <Box sx={statusDot(accentColor)} />
              Active
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box className="icon-box" sx={{ ...iconBoxStyle(accentColor), mb: 1.5 }}>
              <Icon size={22} />
            </Box>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: '14px',
                lineHeight: 1.5,
                transition: 'color 0.3s ease',
              }}
            >
              {product.description}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const BucketSection = ({ number, title, products, index }) => {
    const gridSizes =
      title === 'Quick Win Execution'
        ? { xs: 12, sm: 6, md: 6, lg: 6 }
        : { xs: 12, sm: 6, md: 4, lg: 2.4 };
    const gridSpacing = 2;
  
    return (
      <Box 
        sx={{ 
          mb: 3.125,
          animation: `${fadeInUp} 0.8s ease-out`,
          animationDelay: `${index * 0.2}s`,
          animationFillMode: 'both',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2.5,
            pb: 1.5,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: 1,
              background: 'linear-gradient(90deg, transparent 0%, rgba(233, 30, 140, 0.1) 20%, rgba(233, 30, 140, 0.1) 80%, transparent 100%)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -2,
              left: 0,
              width: '200px',
              height: 3,
              background: 'linear-gradient(90deg, #e91e8c 0%, #c71e6f 50%, transparent 100%)',
              borderRadius: '2px',
              animation: `${shimmer} 3s ease-in-out infinite`,
              boxShadow: '0 0 20px rgba(233, 30, 140, 0.5)',
            },
          }}
        >
          {/* Number Badge */}
          <Box
            sx={{
              position: 'relative',
              width: 48,
              height: 48,
              background: 'linear-gradient(135deg, #e91e8c 0%, #c71e6f 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
              fontWeight: 500,
              mr: 2.25,
              boxShadow: '0 8px 24px rgba(233, 30, 140, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
              color: '#ffffff',
              animation: `${scaleIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1)`,
              animationDelay: `${index * 0.2}s`,
              animationFillMode: 'both',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid rgba(255,255,255,0.1)',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: `${shimmer} 3s ease-in-out infinite`,
              },
              '&:hover': {
                transform: 'scale(1.1) rotate(5deg)',
                boxShadow: '0 12px 32px rgba(233, 30, 140, 0.6), inset 0 1px 0 rgba(255,255,255,0.3)',
              },
            }}
          >
            {number}
          </Box>
  
          {/* Title */}
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontSize: '22px', 
                fontWeight: 500, 
                background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 50%, #e0e0e0 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '1px',
                animation: `${slideInLeft} 0.8s ease-out`,
                animationDelay: `${index * 0.2 + 0.1}s`,
                animationFillMode: 'both',
                textShadow: '0 2px 10px rgba(255,255,255,0.1)',
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -4,
                  left: 0,
                  width: 0,
                  height: 2,
                  background: 'linear-gradient(90deg, #e91e8c, #c71e6f)',
                  transition: 'width 0.4s ease',
                },
                '&:hover::after': {
                  width: '100%',
                },
              }}
            >
              {title}
            </Typography>
          </Box>
  
          {/* Decorative Element */}
          <Box
            sx={{
              width: 60,
              height: 2,
              background: 'linear-gradient(90deg, #e91e8c, transparent)',
              borderRadius: 1,
              opacity: 0.6,
              ml: 2,
            }}
          />
        </Box>
  
        <Grid container spacing={gridSpacing}>
          {products.map((product, idx) => (
            <Grid item {...gridSizes} key={product.id}>
              <ProductCard product={product} index={idx} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
        color: '#ffffff',
        py: 2.5,
        px: 2.5,
      }}
    >
      <Container maxWidth="xl">
        {/* Logo with float animation */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'flex-start', 
            mb: 3.75, 
            ml: -1,
            animation: `${fadeInUp} 0.8s ease-out`,
          }}
        >
          <Box
            component="img"
            src={klogo}
            alt="Kearney"
            sx={{ 
              height: 40, 
              width: 'auto', 
              opacity: 0.9,
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              '&:hover': {
                opacity: 1,
                transform: 'scale(1.05)',
              },
            }}
          />
        </Box>

        {/* Header with staggered animation */}
        <Box 
          sx={{ 
            mb: 3.75, 
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: isMobile ? '28px' : '36px',
              fontWeight: 700,
              mb: 1.25,
              background: 'linear-gradient(90deg, #ffffff 0%, #e91e8c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: `${fadeInUp} 1s ease-out`,
            }}
          >
            AI-Enabled Value Identification & Execution Toolkit
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              color: '#b0b0b0',
              maxWidth: '900px',
              lineHeight: 1.5,
              mx: 'auto',
              animation: `${fadeInUp} 1s ease-out 0.2s`,
              animationFillMode: 'both',
            }}
          >
            A unified toolkit spanning opportunity discovery, wave prioritization, and digital sourcing
            execution—accelerating synergy realization with rapid, high-confidence value capture.
          </Typography>
        </Box>

        {/* Bucket Sections with staggered animations */}
        <BucketSection number="1" title="Synergy Value Discovery" products={products.identifyValue} index={0} />
        <BucketSection number="2" title="Quick Win Execution" products={products.quickWins} index={1} />
        <BucketSection number="3" title="Full Sourcing Execution" products={products.buyBetterWaves} index={2} />
      </Container>
    </Box>
  );
};

export default SPIHome;